import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Stack,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import {
  listAssignments,
  getDefaulters,
  generateReport,
  listReports,
  getReport,
  downloadReportPdf,
  notifyReport,
  notifySingle,
  getReportNotificationStatus,
} from "../../services/gfmService";
import departmentService from "../../services/departmentService";
import { getTeacherClasses } from "../../services/teacherService";
import { usePermissions } from "../../hooks/usePermissions";

/**
 * GFM (Guardian Faculty Member) — Defaulter Dashboard.
 *
 * Backing endpoints (services/gfmService.js):
 *   GET  /gfm/defaulters                          → getDefaulters({ startDate, endDate, assignmentId? })
 *   GET  /gfm/assignments                         → listAssignments()   (populate report selector)
 *   POST /gfm/assignments/:id/reports             → generateReport(assignmentId, { startDate, endDate })
 *   GET  /gfm/reports                             → listReports({ assignmentId?, page, pageSize })
 *   GET  /gfm/reports/:reportId                   → getReport(reportId)  (report + snapshot defaulters)
 *   GET  /gfm/reports/:reportId/pdf               → downloadReportPdf(reportId) [blob]
 *   POST /gfm/reports/:reportId/notify            → notifyReport(reportId, { force, studentIds? })
 *   POST /gfm/reports/:reportId/notify/:studentId → notifySingle(reportId, studentId, { force })
 *
 * The route is wrapped in
 *   PermissionBasedRoute requiredAnyPermission={['gfm.mentees.view','gfm.view_all']}
 * (App.js). Notify / generate actions are additionally gated server-side by
 * `gfm.notify` / `gfm.report.generate`; a caller lacking those simply gets a
 * toast error rather than a crash.
 *
 * Follows the no-crash frontend rules: every async call is wrapped in try/catch,
 * failures surface as a toast (and inline Alert where relevant) — never a white
 * screen — all external response access is optional-chained with safe defaults,
 * and every collection is guarded before iteration.
 *
 * _Requirements: 7.1, 7.5, 7.6, 8.1, 8.4, 9.2, 9.3, 4.9, 10.1_
 */

// ─── Date-range presets ───────────────────────────────────────────────────
// Windows computed with dayjs in the browser's local zone; the backend
// re-anchors the inclusive range to IST 00:00:00 → 23:59:59 (Req 7.1). We only
// send calendar dates (YYYY-MM-DD), so browser-zone drift is not an issue.
const DATE_FMT = "YYYY-MM-DD";

const PRESETS = [
  { value: "THIS_WEEK_MON_WED", label: "This week (Mon–Wed)" },
  { value: "LAST_WEEK_THU_SAT", label: "Last week (Thu–Sat)" },
  { value: "LAST_7_DAYS", label: "Last 7 days" },
  { value: "LAST_MONTH", label: "Last month" },
  { value: "CUSTOM", label: "Custom range" },
];

// ISO Monday of the week containing `d`, computed WITHOUT the dayjs isoWeek
// plugin (which isn't loaded). `dayjs().day()` returns 0=Sun..6=Sat; the number
// of days back to the ISO Monday is (day + 6) % 7. This is correct on every
// weekday INCLUDING Sunday (Sunday → 6 days back to that week's Monday), so it
// never produces a future Monday the way `today.day(1)` did.
const isoMondayOf = (d) => {
  const dow = d.day(); // 0 (Sun) .. 6 (Sat)
  const daysBackToMonday = (dow + 6) % 7;
  return d.subtract(daysBackToMonday, "day").startOf("day");
};

// Returns { startDate, endDate } as YYYY-MM-DD strings for a given preset.
// Never throws — falls back to "last 7 days" for an unknown preset.
const computePresetRange = (preset) => {
  const today = dayjs();
  switch (preset) {
    case "THIS_WEEK_MON_WED": {
      // Monday–Wednesday of the CURRENT ISO week (Mon-start), robust on Sundays.
      const monday = isoMondayOf(today);
      const start = monday;
      const end = monday.add(2, "day"); // Wednesday
      return { startDate: start.format(DATE_FMT), endDate: end.format(DATE_FMT) };
    }
    case "LAST_WEEK_THU_SAT": {
      // Thursday–Saturday of the PREVIOUS ISO week.
      const thisMonday = isoMondayOf(today);
      const lastThursday = thisMonday.subtract(4, "day"); // prev week Thu (Mon - 4)
      const start = lastThursday;
      const end = lastThursday.add(2, "day"); // prev week Sat
      return { startDate: start.format(DATE_FMT), endDate: end.format(DATE_FMT) };
    }
    case "LAST_MONTH": {
      const firstOfThisMonth = today.startOf("month");
      const prevMonth = firstOfThisMonth.subtract(1, "month");
      const start = prevMonth.startOf("month");
      const end = prevMonth.endOf("month");
      return { startDate: start.format(DATE_FMT), endDate: end.format(DATE_FMT) };
    }
    case "LAST_7_DAYS":
    default: {
      const end = today;
      const start = today.subtract(6, "day"); // inclusive 7-day window
      return { startDate: start.format(DATE_FMT), endDate: end.format(DATE_FMT) };
    }
  }
};

const REPORTS_PAGE_SIZE = 10;

// Best-effort extraction of a list array from a response body.
const pickList = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
};

const teacherDisplayName = (assignment) => {
  const teacher = assignment?.teacher || assignment?.Teacher || {};
  const user = teacher.user || teacher.User || teacher || {};
  const first = user.firstName || user.first_name || "";
  const last = user.lastName || user.last_name || "";
  const full = `${first} ${last}`.trim();
  return (
    user.name ||
    user.fullName ||
    (full || null) ||
    user.email ||
    (assignment?.teacherUserId != null
      ? `User #${assignment.teacherUserId}`
      : `Assignment #${assignment?.id ?? "?"}`)
  );
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY") : String(v);
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD MMM YYYY HH:mm") : String(v);
};

const GfmDefaulterDashboard = () => {
  // ─── Date range state ───
  const [preset, setPreset] = useState("LAST_7_DAYS");
  const initialRange = computePresetRange("LAST_7_DAYS");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);

  // ─── Live defaulters state ───
  const [defaulters, setDefaulters] = useState([]);
  const [threshold, setThreshold] = useState(null);
  const [defaultersTotal, setDefaultersTotal] = useState(0);
  const [defaultersLoading, setDefaultersLoading] = useState(false);
  const [defaultersError, setDefaultersError] = useState(null);

  // ─── Assignment selector (for report generation) ───
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");

  // ─── Department / Class filter (narrows the in-scope mentee list) ───
  // `gfm.view_all` == institute-wide scope (admin / superAdmin): they get a
  // Department picker plus a Class picker. Everyone else (hod / teacher with
  // only `gfm.mentees.view`) gets a Class picker scoped server-side to their own
  // classes/mentees. The backend only ever intersects with the caller's scope,
  // so these dropdowns can never widen what a user sees.
  const { hasPermission } = usePermissions();
  const canViewAll = hasPermission("gfm.view_all");

  // ─── Institute context (Super Admin dropdown-select flow) ───
  // A Super Admin who selects an institute keeps their SA token (no operating
  // institute id), so requests must carry the acting institute id. The GFM
  // service threads it as an `instituteId` query param for its own calls; here
  // we pass it to the department-names lookup so the picker is scoped to the
  // acting institute instead of returning every institute's departments
  // (the super-admin default). Null-safe: auth user's own institute →
  // selected institute → null.
  const authUser = useSelector((s) => s?.auth?.user);
  const selectedInstitute = useSelector(
    (s) => s?.instituteContext?.selectedInstitute
  );
  const instituteId = authUser?.instituteId ?? selectedInstitute?.id ?? null;
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");

  // ─── Report generation ───
  const [generating, setGenerating] = useState(false);

  // ─── Report history ───
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  // Compact per-report delivery roll-up for the history table:
  //   Map<reportId, { email: {sent, total}, whatsapp: {sent, total}, loading }>
  const [reportStatusSummaries, setReportStatusSummaries] = useState(new Map());
  const [reportsPage, setReportsPage] = useState(1); // 1-indexed
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null);

  // ─── Per-report / per-student action busy tracking ───
  const [downloadingId, setDownloadingId] = useState(null);
  const [notifyingReportId, setNotifyingReportId] = useState(null);
  const [notifyForce, setNotifyForce] = useState(false);

  // ─── Institute-level WhatsApp toggle (effective) ───
  // Controls whether the WhatsApp channel is surfaced in this page. Defaults to
  // `true` so the UI is unchanged until the config load resolves / if it fails.
  // When the institute has opted out (effective false), WhatsApp badges,
  // summary chips, and reasons are hidden and only Email is shown.
  const [whatsappEnabled, setWhatsappEnabled] = useState(true);

  // ─── Selected report detail (for single-student notify) ───
  const [activeReport, setActiveReport] = useState(null); // { report, defaulters:[] }
  const [reportDetailLoading, setReportDetailLoading] = useState(false);
  const [notifyingStudentId, setNotifyingStudentId] = useState(null);
  // Per-student delivery status for the active report:
  //   Map<studentId, { email:{status,reason,...}|null, whatsapp:{...}|null }>
  const [notificationStatus, setNotificationStatus] = useState(new Map());

  // ─── Validate the window before firing any request (Req 7.2 / 8.2) ───
  const windowError = useMemo(() => {
    if (!startDate || !endDate) return "Select both a start and end date.";
    const s = dayjs(startDate);
    const e = dayjs(endDate);
    if (!s.isValid() || !e.isValid()) return "Invalid date value.";
    if (s.isAfter(e)) return "Start date must be on or before the end date.";
    if (e.diff(s, "day") > 366) return "Window cannot exceed 366 days.";
    return null;
  }, [startDate, endDate]);

  // ─── Fetch live defaulters ───
  const fetchDefaulters = useCallback(async () => {
    if (windowError) {
      setDefaultersError(windowError);
      return;
    }
    setDefaultersLoading(true);
    setDefaultersError(null);
    try {
      const params = { startDate, endDate };
      if (selectedAssignmentId) params.assignmentId = selectedAssignmentId;
      if (canViewAll && selectedDepartmentId) params.departmentId = selectedDepartmentId;
      if (selectedClassId) params.classId = selectedClassId;
      const res = await getDefaulters(params);
      const data = res?.data?.data ?? res?.data ?? {};
      const list = Array.isArray(data.defaulters) ? data.defaulters : [];
      setDefaulters(list);
      setThreshold(data.threshold ?? null);
      setDefaultersTotal(Number(data.total ?? list.length ?? 0));
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load live defaulters.";
      setDefaultersError(msg);
      setDefaulters([]);
      setThreshold(null);
      setDefaultersTotal(0);
      toast.error(msg);
    } finally {
      setDefaultersLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedAssignmentId,
    selectedDepartmentId,
    selectedClassId,
    canViewAll,
    windowError,
  ]);

  // ─── Fetch departments (admin / superAdmin only) ───
  // Institute-scoped id+name list; non-fatal if it fails (picker just stays
  // empty and the class picker still works). Only fetched for `gfm.view_all`.
  const fetchDepartments = useCallback(async () => {
    if (!canViewAll) return;
    try {
      const res = instituteId
        ? await departmentService.getDepartmentNames(instituteId)
        : await departmentService.getDepartmentNames();
      const data = res?.data?.data ?? res?.data ?? {};
      const items =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.departments) && data.departments) ||
        (Array.isArray(data?.rows) && data.rows) ||
        (Array.isArray(data?.items) && data.items) ||
        [];
      setDepartments(items);
    } catch (err) {
      console.error(
        "[GfmDefaulterDashboard] fetchDepartments failed:",
        err?.response?.data?.message || err?.message || err
      );
      setDepartments([]);
    }
  }, [canViewAll, instituteId]);

  // ─── Fetch classes (role-scoped) ───
  // `/teacher/class-names` is already scoped server-side: teacher → own classes,
  // hod → own department's classes, admin → all (optionally filtered by the
  // selected departmentId). We only pass departmentId for `view_all` callers.
  const fetchClasses = useCallback(async () => {
    setClassesLoading(true);
    try {
      const params = {};
      if (canViewAll && selectedDepartmentId) params.departmentId = selectedDepartmentId;
      const res = await getTeacherClasses(params);
      const data = res?.data?.data ?? res?.data ?? {};
      const items =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.classes) && data.classes) ||
        (Array.isArray(data?.rows) && data.rows) ||
        (Array.isArray(data?.items) && data.items) ||
        [];
      setClasses(items);
    } catch (err) {
      console.error(
        "[GfmDefaulterDashboard] fetchClasses failed:",
        err?.response?.data?.message || err?.message || err
      );
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  }, [canViewAll, selectedDepartmentId]);

  // ─── Fetch assignments (for the report generate selector) ───
  const fetchAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const res = await listAssignments({ page: 1, pageSize: 500, isActive: true });
      const data = res?.data?.data ?? res?.data ?? {};
      const items = pickList(data, ["items", "rows"]);
      setAssignments(items);
    } catch (err) {
      // Non-fatal — the selector simply stays empty; live defaulters still work.
      const msg =
        err?.response?.data?.message || "Failed to load GFM assignments.";
      console.error("[GfmDefaulterDashboard] fetchAssignments failed:", msg);
      setAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  }, []);

  // ─── Fetch report history ───
  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const params = { page: reportsPage, pageSize: REPORTS_PAGE_SIZE };
      if (selectedAssignmentId) params.assignmentId = selectedAssignmentId;
      const res = await listReports(params);
      const data = res?.data?.data ?? res?.data ?? {};
      const items = pickList(data, ["items", "rows"]);
      setReports(items);
      setReportsTotal(Number(data.total ?? items.length ?? 0));
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load report history.";
      setReportsError(msg);
      setReports([]);
      setReportsTotal(0);
      toast.error(msg);
    } finally {
      setReportsLoading(false);
    }
  }, [reportsPage, selectedAssignmentId]);

  // ─── Load a compact delivery roll-up for each report in the history page ───
  // Best-effort and parallel: a failure for one report just leaves its cell
  // blank. Summaries are keyed by reportId and recomputed whenever the visible
  // report set changes.
  useEffect(() => {
    let cancelled = false;
    const idsInPage = (Array.isArray(reports) ? reports : [])
      .map((r) => r?.id ?? r?.reportId ?? r?.uuid)
      .filter((x) => x != null);

    if (idsInPage.length === 0) {
      setReportStatusSummaries(new Map());
      return;
    }

    // Mark all as loading up front so the UI can show a spinner per row.
    setReportStatusSummaries((prev) => {
      const next = new Map();
      for (const id of idsInPage) {
        next.set(id, prev.get(id) || { loading: true, email: null, whatsapp: null });
      }
      return next;
    });

    const summarize = (byStudent) => {
      const list = Array.isArray(byStudent) ? byStudent : [];
      const acc = { email: { sent: 0, total: 0 }, whatsapp: { sent: 0, total: 0 } };
      for (const row of list) {
        for (const ch of ["email", "whatsapp"]) {
          const info = row?.[ch];
          // Only a real delivery attempt counts toward the "attempted" total:
          // `sent` or `failed`. A `skipped` attempt (e.g. already notified, or
          // missing contact) is NOT an attempt, so it must not show as 0/1.
          if (info && (info.status === "sent" || info.status === "failed")) {
            acc[ch].total += 1;
            if (info.status === "sent") acc[ch].sent += 1;
          }
        }
      }
      return acc;
    };

    (async () => {
      await Promise.all(
        idsInPage.map(async (id) => {
          try {
            const res = await getReportNotificationStatus(id);
            const data = res?.data?.data ?? res?.data ?? {};
            const acc = summarize(data.byStudent);
            if (cancelled) return;
            // Institute-level WhatsApp toggle rides along on this response so the
            // (view-only) dashboard can hide WhatsApp without needing gfm.manage.
            if (typeof data.whatsappEnabled === "boolean") {
              setWhatsappEnabled(data.whatsappEnabled);
            }
            setReportStatusSummaries((prev) => {
              const next = new Map(prev);
              next.set(id, { loading: false, email: acc.email, whatsapp: acc.whatsapp });
              return next;
            });
          } catch (err) {
            if (cancelled) return;
            // Non-critical secondary read — degrade to no-summary for this row.
            setReportStatusSummaries((prev) => {
              const next = new Map(prev);
              next.set(id, { loading: false, email: null, whatsapp: null });
              return next;
            });
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [reports]);

  // Load assignments once; defaulters + reports react to their deps.
  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Load the department picker options once (admin / superAdmin only).
  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Load class options; refetches when the selected department changes (admin)
  // or the caller's scope resolves.
  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // When the department changes, clear a now-inconsistent class selection so we
  // never send a classId that isn't in the chosen department.
  useEffect(() => {
    setSelectedClassId("");
  }, [selectedDepartmentId]);

  useEffect(() => {
    fetchDefaulters();
  }, [fetchDefaulters]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ─── Preset change → recompute dates (custom leaves fields editable) ───
  const handlePresetChange = (value) => {
    setPreset(value);
    if (value !== "CUSTOM") {
      const range = computePresetRange(value);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  };

  // ─── Generate report (persist + PDF) ───
  const handleGenerateReport = async () => {
    if (windowError) {
      toast.error(windowError);
      return;
    }
    if (!selectedAssignmentId) {
      toast.info("Select an assignment to generate a report for.");
      return;
    }
    setGenerating(true);
    try {
      await generateReport(selectedAssignmentId, { startDate, endDate });
      toast.success("Report generated.");
      setReportsPage(1);
      // Refresh history (fetchReports fires via reportsPage effect if it changed;
      // call directly to cover the case where it was already page 1).
      fetchReports();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to generate report.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Download a report PDF (blob → object URL → trigger download) ───
  const handleDownloadPdf = async (report) => {
    const reportId = report?.id ?? report?.reportId ?? report?.uuid;
    if (!reportId) {
      toast.error("Cannot download: report has no id.");
      return;
    }
    setDownloadingId(reportId);
    let objectUrl = null;
    try {
      const res = await downloadReportPdf(reportId);
      const blobData = res?.data;
      if (!blobData) {
        toast.error("No PDF content returned.");
        return;
      }
      const blob =
        blobData instanceof Blob
          ? blobData
          : new Blob([blobData], { type: "application/pdf" });
      objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      const stamp =
        report?.windowStartDate && report?.windowEndDate
          ? `${dayjs(report.windowStartDate).format(DATE_FMT)}_${dayjs(
              report.windowEndDate
            ).format(DATE_FMT)}`
          : String(reportId);
      link.download = `gfm-defaulter-report-${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to download the report PDF.";
      toast.error(msg);
    } finally {
      if (objectUrl) {
        // Give the browser a tick to start the download before revoking.
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(objectUrl);
          } catch (e) {
            /* no-op */
          }
        }, 1000);
      }
      setDownloadingId(null);
    }
  };

  // ─── Bulk notify (whole report) ───
  // Aggregate distinct per-channel skip/fail reasons from a notify `details`
  // array into a short human hint, e.g. "(whatsapp: whatsapp-not-configured)".
  // Returns "" when there is nothing noteworthy to report. Fully null-safe.
  // When WhatsApp is disabled for the institute, drop the whatsapp channel from
  // the notify `details` so counts and reason hints reflect email only (the
  // whatsapp channel is skipped server-side with 'whatsapp-disabled-for-institute').
  const visibleNotifyDetails = (details) => {
    const list = Array.isArray(details) ? details : [];
    return whatsappEnabled ? list : list.filter((d) => d?.channel !== "whatsapp");
  };

  // Recompute {sent, skipped, failed} from the (channel-filtered) details so the
  // toast counts match what the user can actually see. Falls back to the given
  // server totals when details are unavailable.
  const countFromDetails = (details, fallback) => {
    const list = Array.isArray(details) ? details : [];
    if (list.length === 0) return fallback;
    const acc = { sent: 0, skipped: 0, failed: 0 };
    for (const d of list) {
      const status = String(d?.status || "").toLowerCase();
      if (status === "sent") acc.sent += 1;
      else if (status === "skipped") acc.skipped += 1;
      else if (status === "failed") acc.failed += 1;
    }
    return acc;
  };

  const summarizeNotifyReasons = (details) => {
    const list = visibleNotifyDetails(details);
    const byChannel = new Map();
    for (const d of list) {
      if (!d || d.status === "sent" || !d.reason) continue;
      const channel = d.channel ?? "?";
      if (!byChannel.has(channel)) byChannel.set(channel, new Set());
      byChannel.get(channel).add(String(d.reason));
    }
    if (byChannel.size === 0) return "";
    const parts = [];
    for (const [channel, reasons] of byChannel.entries()) {
      parts.push(`${channel}: ${Array.from(reasons).join("/")}`);
    }
    return `(${parts.join("; ")})`;
  };

  const handleNotifyReport = async (report) => {
    const reportId = report?.id ?? report?.reportId ?? report?.uuid;
    if (!reportId) {
      toast.error("Cannot notify: report has no id.");
      return;
    }
    setNotifyingReportId(reportId);
    try {
      const res = await notifyReport(reportId, { force: notifyForce });
      const data = res?.data?.data ?? res?.data ?? {};
      const counts = countFromDetails(visibleNotifyDetails(data.details), {
        sent: Number(data.sent ?? 0),
        skipped: Number(data.skipped ?? 0),
        failed: Number(data.failed ?? 0),
      });
      const sent = Number(counts.sent ?? 0);
      const skipped = Number(counts.skipped ?? 0);
      const failed = Number(counts.failed ?? 0);
      const hint = summarizeNotifyReasons(data.details);
      const suffix = hint ? ` ${hint}` : "";
      if (failed > 0) {
        toast.warn(
          `Notifications: ${sent} sent, ${skipped} skipped, ${failed} failed.${suffix}`
        );
      } else if (sent === 0 && skipped > 0) {
        // Nothing actually delivered — make the reason visible (e.g. WhatsApp
        // not configured on the server, or missing parent contact).
        toast.warn(`Notifications: nothing sent, ${skipped} skipped.${suffix}`);
      } else {
        toast.success(`Notifications: ${sent} sent, ${skipped} skipped.${suffix}`);
      }
      // Refresh delivery status: the per-student detail column (if this report
      // is open) AND the compact history roll-up — fetchNotificationStatus
      // updates both maps in one call.
      fetchNotificationStatus(reportId);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to send notifications.";
      toast.error(msg);
    } finally {
      setNotifyingReportId(null);
    }
  };

  // ─── Load per-student email/WhatsApp delivery status for a report ───
  // Best-effort: a failure just leaves the status column blank (never blocks
  // viewing defaulters and never toasts an error for this secondary read).
  const fetchNotificationStatus = async (reportId) => {
    if (!reportId) return;
    try {
      const res = await getReportNotificationStatus(reportId);
      const data = res?.data?.data ?? res?.data ?? {};
      if (typeof data.whatsappEnabled === "boolean") {
        setWhatsappEnabled(data.whatsappEnabled);
      }
      const list = Array.isArray(data.byStudent) ? data.byStudent : [];
      const map = new Map();
      const acc = { email: { sent: 0, total: 0 }, whatsapp: { sent: 0, total: 0 } };
      for (const row of list) {
        const sid = row?.studentId;
        if (sid == null) continue;
        map.set(sid, { email: row?.email ?? null, whatsapp: row?.whatsapp ?? null });
        for (const ch of ["email", "whatsapp"]) {
          const info = row?.[ch];
          // Only sent/failed count as an attempt; `skipped` is excluded so a
          // skipped-only channel renders as "—" instead of a misleading 0/1.
          if (info && (info.status === "sent" || info.status === "failed")) {
            acc[ch].total += 1;
            if (info.status === "sent") acc[ch].sent += 1;
          }
        }
      }
      setNotificationStatus(map);
      // Keep the history roll-up in sync so it reflects this fetch too.
      setReportStatusSummaries((prev) => {
        const next = new Map(prev);
        next.set(reportId, { loading: false, email: acc.email, whatsapp: acc.whatsapp });
        return next;
      });
    } catch (err) {
      // Non-critical secondary read — degrade to no status silently.
      console.error("[gfm] notification status load failed:", err?.message || err);
      setNotificationStatus(new Map());
    }
  };

  // ─── Open a report's detail (snapshot defaulters) for single-student notify ───
  const handleViewReport = async (report) => {
    const reportId = report?.id ?? report?.reportId ?? report?.uuid;
    if (!reportId) return;
    setReportDetailLoading(true);
    setActiveReport(null);
    setNotificationStatus(new Map());
    try {
      const res = await getReport(reportId);
      const data = res?.data?.data ?? res?.data ?? {};
      // snapshot may live at data.snapshot, data.defaulters, or data.report.snapshot
      const snapshot =
        (Array.isArray(data.snapshot) && data.snapshot) ||
        (Array.isArray(data.defaulters) && data.defaulters) ||
        (Array.isArray(data?.report?.snapshot) && data.report.snapshot) ||
        [];
      setActiveReport({
        report: data.report ?? data ?? report,
        reportId,
        defaulters: snapshot,
      });
      // Load delivery status alongside the detail (non-blocking).
      fetchNotificationStatus(reportId);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to load report details.";
      toast.error(msg);
      setActiveReport(null);
    } finally {
      setReportDetailLoading(false);
    }
  };

  // ─── Notify a single student on the active report ───
  const handleNotifySingle = async (studentId) => {
    const reportId = activeReport?.reportId;
    if (!reportId || studentId == null) {
      toast.error("Cannot notify: missing report or student.");
      return;
    }
    setNotifyingStudentId(studentId);
    try {
      const res = await notifySingle(reportId, studentId, { force: notifyForce });
      const data = res?.data?.data ?? res?.data ?? {};
      // The backend returns a per-channel breakdown under `details`
      // ([{ channel, status, reason? }]), NOT `channels`. Surface the real
      // per-channel outcome so a skipped/failed WhatsApp is visible instead of
      // a misleading blanket "processed".
      const details = visibleNotifyDetails(data.details);
      const anyFailed = details.some((c) => c?.status === "failed");
      const anySent = details.some((c) => c?.status === "sent");
      const summary = details
        .map(
          (c) =>
            `${c?.channel ?? "?"}=${c?.status ?? "?"}` +
            (c?.reason && c.status !== "sent" ? ` (${c.reason})` : "")
        )
        .join(", ");

      if (details.length === 0) {
        toast.info("Nothing to notify for this student.");
      } else if (anyFailed) {
        toast.error(`Notification: ${summary}`);
      } else if (!anySent) {
        // Everything skipped (e.g. WhatsApp not configured, missing contact).
        toast.warn(`Notification: ${summary}`);
      } else {
        toast.success(`Notification: ${summary}`);
      }
      // Refresh the delivery-status column so the UI reflects this attempt.
      fetchNotificationStatus(reportId);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to notify parent.";
      toast.error(msg);
    } finally {
      setNotifyingStudentId(null);
    }
  };

  const studentIdOf = (d) =>
    d?.studentId ?? d?.id ?? d?.student?.id ?? null;

  // Map a delivery status to an MUI Chip color.
  const statusChipColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "skipped":
        return "warning";
      default:
        return "default";
    }
  };

  // Render a single channel's delivery chip (email / whatsapp). Shows a muted
  // "not sent" chip when there is no attempt yet; a tooltip carries the reason +
  // recipient + time. The leading icon makes the channel obvious at a glance.
  const renderChannelChip = (channel, info) => {
    const icon =
      channel === "whatsapp" ? (
        <WhatsAppIcon fontSize="small" />
      ) : (
        <EmailIcon fontSize="small" />
      );
    const label = channel === "whatsapp" ? "WhatsApp" : "Email";

    if (!info || !info.status) {
      return (
        <Chip
          size="small"
          icon={icon}
          variant="outlined"
          label={`${label}: not sent`}
          sx={{ mr: 0.5, mb: 0.5, color: "text.disabled" }}
        />
      );
    }
    const parts = [];
    if (info.reason) parts.push(info.reason);
    if (info.recipient) parts.push(info.recipient);
    if (info.at) parts.push(fmtDateTime(info.at));
    const tip = parts.join(" · ");
    const chip = (
      <Chip
        size="small"
        icon={icon}
        color={statusChipColor(info.status)}
        variant={info.status === "sent" ? "filled" : "outlined"}
        label={`${label}: ${info.status}`}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
    return tip ? (
      <Tooltip title={tip} key={channel}>
        <span>{chip}</span>
      </Tooltip>
    ) : (
      chip
    );
  };

  // Delivery-status cell for one student (email + whatsapp chips).
  const renderDeliveryStatus = (sid) => {
    const st = sid != null ? notificationStatus.get(sid) : null;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
        {renderChannelChip("email", st?.email)}
        {/* WhatsApp hidden when the institute has disabled WhatsApp delivery. */}
        {whatsappEnabled && renderChannelChip("whatsapp", st?.whatsapp)}
      </Box>
    );
  };

  // Compact per-report delivery roll-up chip for the history table.
  // Shows "Email x/y" + "WhatsApp x/y" (sent/attempted). "—" when no attempt yet.
  const renderReportDeliverySummary = (reportId) => {
    const s = reportId != null ? reportStatusSummaries.get(reportId) : null;
    if (!s || s.loading) {
      return <CircularProgress size={14} />;
    }
    const emailTotal = Number(s.email?.total ?? 0);
    // Ignore WhatsApp totals entirely when the institute has disabled WhatsApp.
    const waTotal = whatsappEnabled ? Number(s.whatsapp?.total ?? 0) : 0;
    if (emailTotal === 0 && waTotal === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          Not sent
        </Typography>
      );
    }
    const summaryChip = (label, icon, stat) => {
      const total = Number(stat?.total ?? 0);
      if (total === 0) return null;
      const sent = Number(stat?.sent ?? 0);
      const allSent = sent === total;
      const noneSent = sent === 0;
      return (
        <Chip
          size="small"
          icon={icon}
          color={allSent ? "success" : noneSent ? "error" : "warning"}
          variant={allSent ? "filled" : "outlined"}
          label={`${sent}/${total}`}
          sx={{ mr: 0.5 }}
        />
      );
    };
    return (
      <Tooltip title="Parents notified (sent / attempted)">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {summaryChip("Email", <EmailIcon fontSize="small" />, s.email)}
          {whatsappEnabled &&
            summaryChip("WhatsApp", <WhatsAppIcon fontSize="small" />, s.whatsapp)}
        </Box>
      </Tooltip>
    );
  };

  const reportsPageCount = Math.max(
    1,
    Math.ceil((reportsTotal || 0) / REPORTS_PAGE_SIZE)
  );

  // ─── Render helpers (extracted to avoid nested-ternary render blocks) ───
  const renderLiveDefaulters = () => {
    if (defaultersLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (defaulters.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No defaulters found for the selected window.
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Class</TableCell>
              <TableCell align="right">Overall %</TableCell>
              <TableCell align="right">Threshold</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {defaulters.map((d, idx) => (
              <TableRow key={studentIdOf(d) ?? idx} hover>
                <TableCell>
                  {d?.name || `Student #${studentIdOf(d) ?? "?"}`}
                </TableCell>
                <TableCell>{d?.className || d?.classId || "—"}</TableCell>
                <TableCell align="right">
                  {d?.overallPercent == null ? "—" : `${d.overallPercent}%`}
                </TableCell>
                <TableCell align="right">
                  {d?.thresholdApplied == null ? "—" : `${d.thresholdApplied}%`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderReportActions = (r) => {
    const rid = r?.id ?? r?.reportId ?? r?.uuid;
    return (
      <>
        <Tooltip title="View defaulters">
          <span>
            <IconButton
              size="small"
              onClick={() => handleViewReport(r)}
              disabled={reportDetailLoading}
            >
              <GroupIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download PDF">
          <span>
            <IconButton
              size="small"
              onClick={() => handleDownloadPdf(r)}
              disabled={downloadingId === rid}
            >
              {downloadingId === rid ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Notify all parents">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleNotifyReport(r)}
              disabled={notifyingReportId === rid}
            >
              {notifyingReportId === rid ? (
                <CircularProgress size={16} />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  };

  const renderReportHistory = () => {
    if (reportsLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (reports.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No reports generated yet.
          </Typography>
        </Box>
      );
    }
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Window</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Threshold</TableCell>
                <TableCell align="right">Defaulters</TableCell>
                <TableCell>Generated</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r, idx) => {
                const reportId = r?.id ?? r?.reportId ?? r?.uuid ?? idx;
                return (
                  <TableRow key={reportId} hover>
                    <TableCell>
                      {fmtDate(r?.windowStartDate)} – {fmtDate(r?.windowEndDate)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={r?.windowType || "—"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {r?.effectiveThreshold == null
                        ? "—"
                        : `${r.effectiveThreshold}%`}
                    </TableCell>
                    <TableCell align="right">
                      {Number(r?.defaulterCount ?? 0)}
                    </TableCell>
                    <TableCell>
                      {r?.generatedBy ? (
                        <Chip
                          size="small"
                          label={r.generatedBy}
                          color={
                            r.generatedBy === "cron" ? "default" : "primary"
                          }
                          variant="outlined"
                          sx={{ mr: 0.5 }}
                        />
                      ) : null}
                      {fmtDateTime(r?.createdAt)}
                    </TableCell>
                    <TableCell>{renderReportDeliverySummary(r?.id ?? r?.reportId ?? r?.uuid)}</TableCell>
                    <TableCell align="right">{renderReportActions(r)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {reportsPageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={reportsPageCount}
              page={reportsPage}
              onChange={(_e, value) => setReportsPage(value)}
              size="small"
            />
          </Box>
        )}
      </>
    );
  };

  const renderReportDetail = () => {
    if (reportDetailLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (
      !activeReport ||
      !Array.isArray(activeReport.defaulters) ||
      activeReport.defaulters.length === 0
    ) {
      return (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            This report has no defaulters to notify.
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Class</TableCell>
              <TableCell align="right">Overall %</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell align="right">Notify</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeReport.defaulters.map((d, idx) => {
              const sid = studentIdOf(d);
              return (
                <TableRow key={sid ?? idx} hover>
                  <TableCell>{d?.name || `Student #${sid ?? "?"}`}</TableCell>
                  <TableCell>{d?.className || d?.classId || "—"}</TableCell>
                  <TableCell align="right">
                    {d?.overallPercent == null ? "—" : `${d.overallPercent}%`}
                  </TableCell>
                  <TableCell>{renderDeliveryStatus(sid)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        notifyingStudentId === sid ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <SendIcon fontSize="small" />
                        )
                      }
                      onClick={() => handleNotifySingle(sid)}
                      disabled={sid == null || notifyingStudentId === sid}
                    >
                      Notify
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1400, mx: "auto" }}>
      {/* ─── Header ─── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Defaulter Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review mentee defaulters over a window, generate reports, and notify
            parents.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchDefaulters();
            fetchReports();
          }}
          disabled={defaultersLoading || reportsLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* ─── Controls: date range + assignment selector + generate ─── */}
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4} lg={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="gfm-preset-label">Date range</InputLabel>
              <Select
                labelId="gfm-preset-label"
                label="Date range"
                value={preset}
                onChange={(e) => handlePresetChange(e.target.value)}
              >
                {PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={4} lg={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start"
              value={startDate || ""}
              disabled={preset !== "CUSTOM"}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ sx: { minWidth: 110 } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={4} lg={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End"
              value={endDate || ""}
              disabled={preset !== "CUSTOM"}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ sx: { minWidth: 110 } }}
            />
          </Grid>

          {/* Department filter — admin / superAdmin only (institute-wide scope). */}
          {canViewAll && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="gfm-department-label" shrink>
                  Department
                </InputLabel>
                <Select
                  labelId="gfm-department-label"
                  label="Department"
                  value={selectedDepartmentId}
                  onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>All departments</em>
                  </MenuItem>
                  {(Array.isArray(departments) ? departments : []).map((d) => (
                    <MenuItem key={d.id} value={String(d.id)}>
                      {d.name || d.departmentName || `Department ${d.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Class filter — all roles. Scoped server-side to the caller's classes
              (teacher = own, hod = own department, admin = all / selected dept). */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="gfm-class-label" shrink>
                Class
              </InputLabel>
              <Select
                labelId="gfm-class-label"
                label="Class"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  <em>All classes</em>
                </MenuItem>
                {classesLoading && (
                  <MenuItem value="" disabled>
                    Loading…
                  </MenuItem>
                )}
                {(Array.isArray(classes) ? classes : []).map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.nameWithSection || c.name || `Class ${c.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={9} md={8} lg={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="gfm-assignment-label" shrink>
                Assignment (for reports)
              </InputLabel>
              <Select
                labelId="gfm-assignment-label"
                label="Assignment (for reports)"
                value={selectedAssignmentId}
                onChange={(e) => {
                  setReportsPage(1);
                  setSelectedAssignmentId(e.target.value);
                }}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  <em>All (live view only)</em>
                </MenuItem>
                {assignmentsLoading && (
                  <MenuItem value="" disabled>
                    Loading…
                  </MenuItem>
                )}
                {(Array.isArray(assignments) ? assignments : []).map((a) => (
                  <MenuItem key={a?.id ?? a?.uuid} value={a?.id ?? ""}>
                    {teacherDisplayName(a)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3} md={4} lg={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={
                generating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DescriptionIcon />
                )
              }
              onClick={handleGenerateReport}
              disabled={generating || !!windowError || !selectedAssignmentId}
              sx={{ height: 40, whiteSpace: "nowrap" }}
            >
              Generate report
            </Button>
          </Grid>
        </Grid>

        {windowError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {windowError}
          </Alert>
        )}
        {!selectedAssignmentId && !windowError && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            Select an assignment to enable report generation. The live defaulter
            list below reflects the chosen window across all your mentees.
          </Typography>
        )}
      </Paper>

      {/* ─── Live defaulters ─── */}
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="h6">Live defaulters</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {threshold != null && (
              <Chip
                size="small"
                color="info"
                label={`Threshold: ${threshold}%`}
              />
            )}
            <Chip
              size="small"
              label={`${defaultersTotal} defaulter${
                defaultersTotal === 1 ? "" : "s"
              }`}
            />
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {defaultersError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setDefaultersError(null)}
          >
            {defaultersError}
          </Alert>
        )}

        {renderLiveDefaulters()}
      </Paper>

      {/* ─── Notify options ─── */}
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyForce}
              onChange={(e) => setNotifyForce(e.target.checked)}
            />
          }
          label="Force resend (notify even if a prior notification was already sent)"
        />
      </Paper>

      {/* ─── Report history ─── */}
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="h6">Report history</Typography>
          <Chip
            size="small"
            label={`${reportsTotal} report${reportsTotal === 1 ? "" : "s"}`}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {reportsError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setReportsError(null)}
          >
            {reportsError}
          </Alert>
        )}

        {renderReportHistory()}
      </Paper>

      {/* ─── Active report detail — per-student notify ─── */}
      {(reportDetailLoading || activeReport) && (
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography variant="h6">
              Report defaulters — notify individually
            </Typography>
            {activeReport && (
              <Button
                size="small"
                onClick={() => setActiveReport(null)}
                disabled={notifyingStudentId != null}
              >
                Close
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {renderReportDetail()}
        </Paper>
      )}
    </Box>
  );
};

export default GfmDefaulterDashboard;
